import { useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

import { selectErrorsForKeys, selectIsFetchingForKeys } from '@/reducers/fetch'
import {
  getCurrentUser,
  selectIsCurrentUserScorp,
  selectCurrentPlanServices,
} from '@/selectors/user.selectors'
import { selectIsPayrollActive } from '@/features/payroll/payroll.selectors'
import {
  submitCancelStripeSubscriptionAtPeriodEnd,
  SUMBIT_CANCEL_STRIPE_SUBSCRIPTION_AT_PERIOD_END_KEY,
} from '@/actions/settings/billingActions'
import { sendUserCancellationMessageZap } from '@/actions/zapierActions'

import { usePayrollPrices } from '@/features/payroll/payrollPrice.hooks'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { useLoadedFlagValue, useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useIsDeviceWidth, DeviceWidth } from '@/utils/deviceWidthHelpers'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import {
  CancellationStepIdentifier,
  updateAutomaticCancellationAnalyticsRecord,
} from './lib/userCancellation.slice'
import { isEligibleForDiscount } from './lib/userCancellation.selectors'
import {
  CANCELLATION_FLOW_STEP,
  useGetCostComparisonPageData,
  useSelfServiceCancellationSteps,
} from './lib/helpers'

import { TextColors, TypographyStyles } from '@/styles'
import { Alert, Card, CardColors, Image, List, Separator, Text } from '@/ui'
import { UserCancellationFlowFooter } from './UserCancellationFlowFooter'
import { HeardItem } from './HeardItem'
import { AlternativeItem } from './AlternativeItem'
import { selectUserHasPermission } from '@/reducers/auth/permission.selectors'
import {
  ProductFeatureId,
  startUserPermissionsRefresh,
} from '@/actions/permissionActions'

export const CostComparison = ({ recordId }: { recordId?: number }) => {
  const dispatch = useAppDispatch()
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const location = useLocation()

  const isLoading = useReselector(selectIsFetchingForKeys, [
    SUMBIT_CANCEL_STRIPE_SUBSCRIPTION_AT_PERIOD_END_KEY,
  ])

  const error = useReselector(selectErrorsForKeys, [
    SUMBIT_CANCEL_STRIPE_SUBSCRIPTION_AT_PERIOD_END_KEY,
  ])

  const enableDiscounts = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableCancellationDiscounts,
    false
  )

  const eligibleForDiscount = useReselector(
    isEligibleForDiscount,
    enableDiscounts
  )
  const currentUser = useReselector(getCurrentUser)

  useEffect(() => startUserPermissionsRefresh(dispatch), [dispatch])

  useEffect(() => {
    const updateRecord = async (recordId?: number) => {
      if (recordId) {
        await updateAutomaticCancellationAnalyticsRecord(recordId, {
          finalStepReached: CancellationStepIdentifier.COST_COMPARISON,
        })(dispatch)
      }
    }
    updateRecord(recordId)
  }, [dispatch, recordId])

  const isScorp = useReselector(selectIsCurrentUserScorp)
  const copyFromTier = useLoadedFlagValue(
    FEATURE_FLAG_KEYS.planCopyFromTier,
    false
  )
  const payrollActive = useReselector(selectIsPayrollActive)
  const planServices = useReselector(selectCurrentPlanServices)
  const hasSCorpCompliancePermission = useReselector(
    selectUserHasPermission,
    ProductFeatureId.sCorpComplianceSupport
  )
  const hasPayrollPermission = useReselector(
    selectUserHasPermission,
    ProductFeatureId.payroll
  )
  const includesSCorpCompliance = copyFromTier
    ? planServices.includes(ProductFeatureId.sCorpComplianceSupport)
    : hasSCorpCompliancePermission
  const isPayrollActive = copyFromTier ? payrollActive : hasPayrollPermission
  const hasQTEPermission = useReselector(
    selectUserHasPermission,
    ProductFeatureId.quarterlyTaxes
  )
  const hasAnnualTaxesPermission = useReselector(
    selectUserHasPermission,
    ProductFeatureId.annualTaxes
  )
  const hasPhoneSupport = useReselector(
    selectUserHasPermission,
    ProductFeatureId.phoneSupport
  )
  const hasBusinessTaxPrepPermission = useReselector(
    selectUserHasPermission,
    ProductFeatureId.annualTaxesBusinessFiling
  )
  const hasTaxConsultationPermission = useReselector(
    selectUserHasPermission,
    ProductFeatureId.taxConsultation
  )
  const hasFreePayrollPermission = useReselector(
    selectUserHasPermission,
    ProductFeatureId.payrollIncludedForFreeForSelf
  )
  // The perk line follows payroll standing only under the flag: the granted-permission
  // reading showed it whether or not the customer ever enrolled.
  const showsFreePayroll = copyFromTier
    ? payrollActive && hasFreePayrollPermission
    : hasFreePayrollPermission

  const goToNextStep = useSelfServiceCancellationSteps(
    CANCELLATION_FLOW_STEP.costComparison
  )

  const { base, seat } = usePayrollPrices()
  const payrollLineTitle =
    base && seat
      ? `Payroll (${base}/mo + ${seat}/person add on)`
      : 'Payroll (add on)'

  const onContinue = useCallback(async () => {
    if (!eligibleForDiscount) {
      const res = await submitCancelStripeSubscriptionAtPeriodEnd(
        location?.state?.cancellationDetails ?? {}
      )(dispatch)
      if (res) {
        sendUserCancellationMessageZap(currentUser, 'submit_cancel')
        goToNextStep()
      }
    } else {
      goToNextStep()
    }
  }, [dispatch, eligibleForDiscount, location, currentUser, goToNextStep])

  const data = useGetCostComparisonPageData(isScorp)
  return (
    <>
      <div className="flex flex-col w-full">
        {error.length > 0 && (
          <Alert title="Error" description={error[0].message} variant="error" />
        )}

        <div className="flex justify-center">
          <Text variant={TypographyStyles.DISPLAY_MD}>
            Compare and save with Heard
          </Text>
        </div>

        <div className="flex justify-center w-full md:w-3/4 lg:w-5/8 mx-auto mt-5">
          <Text variant={TypographyStyles.BODY_LG} textAlign="center">
            Paying for services a la carte adds up fast. Heard saves money by
            giving you everything you need all in one place.
          </Text>
        </div>

        <div className="flex flex-row justify-around gap-8 mt-10">
          <Card color={CardColors.PRIORITY} className="grow-1 w-1/2">
            <Text
              variant={TypographyStyles.DISPLAY_MD}
              color={TextColors.GREEN}
            >
              Heard
            </Text>

            <Text
              variant={TypographyStyles.BODY_XS}
              color={TextColors.MUTED}
              className="mt-1"
            >
              {data.subText}
            </Text>

            <Separator className="mt-4" />

            <List>
              <HeardItem title="Online access to bookkeeping software" />
              <HeardItem title="Expert done-for-you monthly bookkeeping" />
              <HeardItem title="Monthly financial insights and reports" />

              {hasQTEPermission && (
                <HeardItem title="Personalized federal and state quarterly tax estimates" />
              )}

              {hasAnnualTaxesPermission && (
                <HeardItem title="Annual personal federal and state income tax returns" />
              )}

              {hasPhoneSupport ? (
                <HeardItem title="Unlimited messaging and phone support for all financial matters" />
              ) : (
                <HeardItem title="Unlimited messaging support for bookkeeping and general platform questions" />
              )}

              {includesSCorpCompliance && (
                <HeardItem title="S corporation set up and compliance" />
              )}

              {isPayrollActive && !hasFreePayrollPermission && (
                <HeardItem title={payrollLineTitle} />
              )}

              {showsFreePayroll && (
                <HeardItem title="Integrated payroll management via Gusto" />
              )}
            </List>

            <Separator />

            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="w-full md:w-1/4">
                <Image
                  alt="piggy bank green"
                  src="https://heard-images.s3.amazonaws.com/assets/piggy-bank-green.svg"
                  height={80}
                  width={80}
                />
              </div>

              <div className="w-full md:w-3/4">
                <Text
                  textAlign={isMobile ? 'left' : 'right'}
                  variant={TypographyStyles.DISPLAY_MD}
                  color={TextColors.GREEN}
                >
                  {data.currentSubscriptionPrice}/
                  {data.isMonthly ? 'mo' : 'year'}
                </Text>
              </div>
            </div>
          </Card>

          <Card
            color={CardColors.PRIORITY}
            className="grow-1 w-1/2 flex flex-col"
          >
            <Text
              variant={TypographyStyles.DISPLAY_MD}
              color={TextColors.GREEN}
            >
              Alternatives
            </Text>

            <Text
              variant={TypographyStyles.BODY_XS}
              color={TextColors.MUTED}
              className="mt-1"
            >
              {data.subText}
            </Text>
            <Separator className="mt-4" />

            <List className="flex-1 flex flex-col justify-between">
              <AlternativeItem
                title="Bookkeeping software"
                price={formatCurrencyFromCents(
                  data.bookkeeping_software_monthly_fee_in_cents,
                  { hideDecimalsIfZero: true }
                )}
                cadence="mo"
              />

              <AlternativeItem
                title="Done for you monthly bookkeeping"
                price={formatCurrencyFromCents(
                  data.done_for_you_monthly_bookkeeping_monthly_fee_in_cents,
                  { hideDecimalsIfZero: true }
                )}
                cadence="mo"
              />

              {hasAnnualTaxesPermission && (
                <AlternativeItem
                  title="Annual personal tax return filing"
                  price={formatCurrencyFromCents(
                    data.annual_personal_tax_return_filing_yearly_fee_in_cents,
                    { hideDecimalsIfZero: true }
                  )}
                  cadence="year"
                />
              )}

              {hasBusinessTaxPrepPermission && (
                <AlternativeItem
                  title="Annual business tax return filing"
                  price={formatCurrencyFromCents(
                    data.annual_business_tax_return_filing_yearly_fee_in_cents,
                    { hideDecimalsIfZero: true }
                  )}
                  cadence="year"
                />
              )}

              {hasTaxConsultationPermission && (
                <AlternativeItem
                  title="Tax advisory with a local CPA"
                  price={formatCurrencyFromCents(
                    data.tax_advisory_with_local_cpa_hourly_fee_in_cents,
                    { hideDecimalsIfZero: true }
                  )}
                  cadence="hour"
                />
              )}

              {includesSCorpCompliance && (
                <AlternativeItem
                  title="S corporation set up and compliance"
                  price={formatCurrencyFromCents(
                    data.s_corp_setup_and_compliance_in_cents,
                    { hideDecimalsIfZero: true }
                  )}
                />
              )}

              {isPayrollActive && !hasFreePayrollPermission && (
                <AlternativeItem
                  title="Payroll"
                  price={formatCurrencyFromCents(
                    data.payroll_monthly_fee_in_cents,
                    { hideDecimalsIfZero: true }
                  )}
                  cadence="mo"
                />
              )}
            </List>

            <Separator />

            <div className="flex items-end">
              <Image
                alt="piggy bank orange"
                src="https://heard-images.s3.amazonaws.com/assets/piggy-bank-orange.svg"
                height={80}
                width={80}
              />
            </div>
          </Card>
        </div>

        <div>
          <Separator className="w-full" />

          <div className="flex justify-center w-full mt-8">
            <Text variant={TypographyStyles.H1}>Did you know?</Text>
          </div>

          <div className="flex justify-center w-full md:w-3/4 lg:w-5/8 mx-auto">
            <Text variant={TypographyStyles.BODY_LG} textAlign="center">
              Right now, you have a locked-in rate of{' '}
              {data.currentSubscriptionPrice}/{data.isMonthly ? 'mo' : 'year'}.
              If you decide to re-join later, you will have to pay an additional
              catch up bookkeeping fee in addition to any increase in rate.
            </Text>
          </div>

          <div className="flex flex-wrap justify-around w-full mt-8 gap-8">
            {data.currentRateCard}
            {data.newMemberRateCard}
          </div>
        </div>
      </div>

      <UserCancellationFlowFooter
        stayLabel="Keep My Plan"
        continueLabel={
          eligibleForDiscount ? undefined : 'Finalize cancellation'
        }
        goToNextStep={onContinue}
        isLoading={isLoading}
      />
    </>
  )
}
